import React, { useState, useEffect } from 'react'; 
import axios from 'axios';
import { CircularProgress } from '@mui/material';

const SchemeWiseCommoditiesSubsidies = () => {
    const [commoditySchemes, setCommoditySchemes] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [offices, setOffices] = useState([]);
    const [fpsList, setFpsList] = useState([]);
    const [formData, setFormData] = useState({ distCode: '0', afsoCode: '0', fpsId: '0' });
    const [subsidyData, setSubsidyData] = useState([]);
    const [inputErrors, setInputErrors] = useState({});
    const [touchedRows, setTouchedRows] = useState({});
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [currentMonthYear, setCurrentMonthYear] = useState({ month: '', year: '' });
    const [showFpsIdColumn, setShowFpsIdColumn] = useState(false);
    const [showOfficeColumn, setShowOfficeColumn] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchCurrentMonthYear();
        fetchDistricts();
        fetchOfficesForAllDistricts();
        fetchCommoditySchemes('0', '0', '0');
    }, []);

    const clearError = () => {
        setErrorMessage('');
    };

    const fetchCurrentMonthYear = async () => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy/current-month-year`);
            const { month, year } = response.data;

            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];

            setCurrentMonthYear({ month: monthNames[month - 1], year });
        } catch (error) {
            console.error('Error fetching month/year:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchDistricts = async () => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy/districts`);
            setDistricts(response.data);
        } catch (error) {
            console.error('Error fetching districts:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchOfficesForAllDistricts = async () => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy/offices?distCode=0`);
            setOffices(response.data);
        } catch (error) {
            console.error('Error fetching offices:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchOfficesByDistrict = async (distCode) => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy/offices?distCode=${distCode}`);
            setOffices(response.data);
        } catch (error) {
            console.error('Error fetching offices:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const fetchFpsByOffice = async (afsoCode) => {
        try {
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy/fps?afsoCode=${afsoCode}`);
            setFpsList(response.data);
        } catch (error) {
            console.error('Error fetching FPS:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        }
    };

    const shouldShowFpsIdColumn = (distCode, afsoCode, fpsId) => {
        return (afsoCode !== '0' && fpsId === '0') || (distCode !== '0' && afsoCode === '0' && fpsId === '0');
    };

    const shouldShowOfficeColumn = (distCode, afsoCode, fpsId) => {
        return distCode !== '0' && afsoCode === '0' && fpsId === '0';
    };

    const fetchCommoditySchemes = async (distCode, afsoCode, fpsId) => {
        try {
            setLoading(true);
            clearError();
            const response = await axios.get(`${process.env.REACT_APP_URL}/api/subsidy/commodity-schemes`, {
                params: { distCode, afsoCode, fpsId }
            });
            
            let data = response.data.map((item, index) => ({ ...item, index }));
            
            const showFps = shouldShowFpsIdColumn(distCode, afsoCode, fpsId);
            const showOffice = shouldShowOfficeColumn(distCode, afsoCode, fpsId);
            
            if (showFps || showOffice) {
                data.sort((a, b) => {
                    if (showOffice && a.afsoNameEn !== b.afsoNameEn) {
                        return a.afsoNameEn.localeCompare(b.afsoNameEn);
                    }
                    if (showFps && a.fpsId !== b.fpsId) {
                        return a.fpsId.localeCompare(b.fpsId);
                    }
                    return a.commNameEn.localeCompare(b.commNameEn);
                });
            } else {
                data.sort((a, b) => a.commNameEn.localeCompare(b.commNameEn));
            }
            
            setCommoditySchemes(data);
            setSubsidyData(data);
            
            const initialErrors = {};
            data.forEach((_, index) => {
                initialErrors[`commoditySale-${index}`] = false;
            });
            setInputErrors(initialErrors);
            setDataLoaded(true);
            
            setShowFpsIdColumn(showFps);
            setShowOfficeColumn(showOffice);
        } catch (error) {
            console.error('Error fetching commodity schemes:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDistrictChange = async (e) => {
        const distCode = e.target.value;
        setFormData(prev => ({ ...prev, distCode, afsoCode: '0', fpsId: '0' }));
        setFpsList([]);
        if (distCode !== '0') {
            await fetchOfficesByDistrict(distCode);
        } else {
            await fetchOfficesForAllDistricts();
        }
        fetchCommoditySchemes(distCode, '0', '0');
    };

    const handleOfficeChange = async (e) => {
        const afsoCode = e.target.value;
        setFormData(prev => ({ ...prev, afsoCode, fpsId: '0' }));
        setFpsList([]);
        if (afsoCode !== '0') {
            await fetchFpsByOffice(afsoCode);
        }
        fetchCommoditySchemes(formData.distCode, afsoCode, '0');
    };

    const handleFpsChange = async (e) => {
        const fpsId = e.target.value;
        setFormData(prev => ({ ...prev, fpsId }));
        fetchCommoditySchemes(formData.distCode, formData.afsoCode, fpsId);
    };

    const formatToTwoDecimals = (value) => {
        if (value === null || value === undefined || value === '' || isNaN(value)) return 0.00;
        return Math.floor(parseFloat(value) * 100) / 100;
    };

    const formatDisplayValue = (value) => formatToTwoDecimals(value).toFixed(2);

    const handleInputChange = (index, field, value) => {
        setTouchedRows(prev => ({ ...prev, [index]: true }));
        const updatedData = [...subsidyData];
        const numValue = value === '' ? '' : parseFloat(value) || 0.00;
        updatedData[index] = { ...updatedData[index], [field]: numValue };
        setSubsidyData(updatedData);
    };

    const isNumber = (evt) => {
        evt = evt || window.event;
        const charCode = evt.which ? evt.which : evt.keyCode;
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(charCode)) return true;
        if (evt.ctrlKey && [65, 67, 86, 88].includes(charCode)) return true;
        const charStr = String.fromCharCode(charCode);
        if (!charStr.match(/[0-9.]/)) { evt.preventDefault(); return false; }
        const currentValue = evt.target.value;
        if (charStr === '.' && currentValue.includes('.')) { evt.preventDefault(); return false; }
        return true;
    };

    const handleDoubleDigitInput = (e, index, field) => {
        let value = e.target.value.replace(/[^0-9.]/g, '');
        const parts = value.split('.');
        if (parts.length > 2) value = parts[0] + '.' + parts.slice(1).join('');
        if (value.includes('.')) {
            const decimalParts = value.split('.');
            if (decimalParts[1].length > 2) value = decimalParts[0] + '.' + decimalParts[1].substring(0, 2);
        }
        handleInputChange(index, field, value);
    };

    const handleBlur = (e, index, field) => {
        if (!touchedRows[index]) return;
        let value = e.target.value;
        if (value === '' || value === '.') value = '0.00';
        let num = parseFloat(value);
        if (isNaN(num)) num = 0;
        else num = Math.floor(num * 100) / 100;
        handleInputChange(index, field, parseFloat(num.toFixed(2)));
        if (field !== 'commSelPrice') validateRowData(index);
    };

    const validateRowData = (index) => {
        if (!touchedRows[index]) return;
        const row = subsidyData[index];
        const centralSubsidy = row.subsidyPrice || 0;
        const stateSubsidy = row.stateSubsidyPrice || 0;
        const economicCost = row.economicPrice || 0;
        const expectedCommSalePrice = economicCost - (centralSubsidy + stateSubsidy);
        const formattedPrice = parseFloat(expectedCommSalePrice.toFixed(2));

        if (formattedPrice < 0.00) {
            setInputErrors(prev => ({ ...prev, [`commoditySale-${index}`]: true }));
            setTimeout(() => {
                alert(`Commodity Sales price cannot be negative for row ${index + 1}.`);
            }, 50);
            return false;
        } else {
            const updatedData = [...subsidyData];
            updatedData[index].commSelPrice = formattedPrice;
            setSubsidyData(updatedData);
            setInputErrors(prev => ({ ...prev, [`commoditySale-${index}`]: false }));
        }
        return true;
    };

    const validate = () => {
        let hasError = false;
        const updatedErrors = { ...inputErrors };
        subsidyData.forEach((row, index) => {
            if (row.subsidyPrice < 0 || isNaN(row.subsidyPrice) ||
                row.stateSubsidyPrice < 0 || isNaN(row.stateSubsidyPrice) ||
                row.economicPrice < 0 || isNaN(row.economicPrice)) {
                hasError = true;
                updatedErrors[`commoditySale-${index}`] = true;
            } else {
                const centralSubsidy = row.subsidyPrice || 0;
                const stateSubsidy = row.stateSubsidyPrice || 0;
                const economicCost = row.economicPrice || 0;
                const commSelPrice = row.commSelPrice || 0;
                const expectedCommSalePrice = parseFloat((economicCost - (centralSubsidy + stateSubsidy)).toFixed(2));
                if (Math.abs(commSelPrice - expectedCommSalePrice) > 0.01) {
                    hasError = true;
                    updatedErrors[`commoditySale-${index}`] = true;
                } else {
                    updatedErrors[`commoditySale-${index}`] = false;
                }
            }
        });
        setInputErrors(updatedErrors);

        if (hasError) {
            alert("Commodity Sale Price differs from the calculated expected value.");
            return false;
        }
        return true;
    };

    const getInputStyle = (index, field) => {
        const baseStyle = {
            textAlign: 'right',
            width: '95%',
            border: '1px solid #ccc',
            padding: '4px',
            fontSize: '13px',
            backgroundColor: '#fff'
        };
        if (inputErrors[`commoditySale-${index}`] && field === 'commSelPrice') {
            return { ...baseStyle, border: '2px solid red' };
        }
        return baseStyle;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const submitData = {
            distCode: formData.distCode,
            afsoCode: formData.afsoCode,
            fpsId: formData.fpsId,
            commIdList: subsidyData.map(item => item.commId),
            schemeIdList: subsidyData.map(item => item.schemeId),
            commoditySaleList: subsidyData.map(item => formatToTwoDecimals(item.commSelPrice)),
            economicCostList: subsidyData.map(item => formatToTwoDecimals(item.economicPrice)),
            centralSubsidyPriceList: subsidyData.map(item => formatToTwoDecimals(item.subsidyPrice)),
            stateSubsidyPriceList: subsidyData.map(item => formatToTwoDecimals(item.stateSubsidyPrice))
        };

        try {
            setLoading(true);
            clearError();
            await axios.post('http://localhost:8083/api/subsidy/save', submitData, {
                headers: { 'Content-Type': 'application/json' }
            });
            alert('Records saved successfully!');
            
            fetchCommoditySchemes(formData.distCode, formData.afsoCode, formData.fpsId);
        } catch (error) {
            console.error('Error saving data:', error);
            if (error.response) {
                setErrorMessage(`Backend Error: ${error.response.status} - ${error.response.data.message || error.response.data}`);
            } else if (error.request) {
                setErrorMessage('Invalid Request (Exception).');
            } else {
                setErrorMessage('Invalid Request (Exception)');
            }
        } finally {
            setLoading(false);
        }
    };

    const totalColumns = 4 + (showOfficeColumn ? 1 : 0) + (showFpsIdColumn ? 1 : 0) + 4; 

    return (
        <div className="wrapper row3" style={{ paddingBottom: '40px', overflow: 'hidden', position: 'relative' }}>
            <div id="container">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                    <table width="80%">
                        <thead>
                            <tr className="tableheader">
                                <th colSpan={6}>Subsidy Rates For Commodities</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ padding: '8px' }}><b>District</b></td>
                                <td style={{ padding: '8px' }}>
                                    <select name="distCode" value={formData.distCode} onChange={handleDistrictChange} style={{ width: '80%', padding: '6px' }} disabled={loading}>
                                        <option value="0">--All--</option>
                                        {districts.map(d => <option key={d.distCode} value={d.distCode}>{d.distNameEn}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: '8px' }}><b>Office</b></td>
                                <td style={{ padding: '8px' }}>
                                    <select name="afsoCode" value={formData.afsoCode} onChange={handleOfficeChange} style={{ width: '80%', padding: '6px' }} disabled={loading}>
                                        <option value="0">--ALL--</option>
                                        {offices.map(o => <option key={o.officeCode} value={o.officeCode}>{o.officeNameEn}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: '8px' }}><b>FPS Id</b></td>
                                <td style={{ padding: '8px' }}>
                                    <select name="fpsId" value={formData.fpsId} onChange={handleFpsChange} style={{ width: '80%', padding: '6px' }} disabled={loading}>
                                        <option value="0">--ALL--</option>
                                        {fpsList.map(f => <option key={f.fpsId} value={f.fpsId}>{f.fpsId}</option>)}
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {errorMessage && (
                   <div style={{ textAlign: 'center', color: 'red', marginTop: '20px' }}>
                       
                            {errorMessage}
                      
                    </div>
                )}

                {loading && (
                    <div id="loading" style={{ textAlign: "center", margin: "20px 0" }}>
                        <CircularProgress/>
                    </div>
                )}

                {dataLoaded && (
                    <div style={{ marginTop: "20px", textAlign: "center" }}>
                        <form onSubmit={handleSubmit}>
                            <table border="1" style={{ margin: "0 auto", width: showOfficeColumn || showFpsIdColumn ? "95%" : "80%", borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr className="tableheader">
                                        <th colSpan={totalColumns}>
                                            Subsidy Rates For Commodities – {currentMonthYear.month}, {currentMonthYear.year}
                                        </th>
                                    </tr>
                                    <tr className="tableheader">
                                        <th style={{ padding: '8px', fontSize: '14px' }}>Sl No.</th>
                                        {showOfficeColumn && (
                                            <th style={{ padding: '8px', fontSize: '14px' }}>Office</th>
                                        )}
                                        {showFpsIdColumn && (
                                            <th style={{ padding: '8px', fontSize: '14px' }}>FPS ID</th>
                                        )}
                                        <th style={{ padding: '8px', fontSize: '14px' }}>Commodity Name</th>
                                        <th style={{ padding: '8px', fontSize: '14px' }}>Scheme Name</th>
                                        <th style={{ padding: '8px', fontSize: '14px' }}>Economic Cost</th>
                                        <th style={{ padding: '8px', fontSize: '14px' }}>Central Subsidy Price</th>
                                        <th style={{ padding: '8px', fontSize: '14px' }}>State Subsidy Price</th>
                                        <th style={{ padding: '8px', fontSize: '14px' }}>Commodity Sales Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subsidyData.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center', padding: '8px' }}>{index + 1}</td>
                                            {showOfficeColumn && (
                                                <td style={{ textAlign: 'center', padding: '8px' }}>
                                                    <strong>{item.afsoNameEn}</strong>
                                                </td>
                                            )}
                                            {showFpsIdColumn && (
                                                <td style={{ textAlign: 'center', padding: '8px' }}>
                                                    <strong>{item.fpsId}</strong>
                                                </td>
                                            )}
                                            <td style={{ textAlign: 'center', padding: '8px' }}><strong>{item.commNameEn}</strong></td>
                                            <td style={{ textAlign: 'center', padding: '8px' }}><strong>{item.schemeNameEn}</strong></td>
                                            <td style={{ textAlign: 'right', padding: '6px' }}>
                                                <input type="text" style={getInputStyle(index, 'economicPrice')} value={formatDisplayValue(item.economicPrice)} onChange={(e) => handleDoubleDigitInput(e, index, 'economicPrice')} onBlur={(e) => handleBlur(e, index, 'economicPrice')} onKeyPress={isNumber} onFocus={(e) => e.target.select()} autoComplete="off" disabled={loading}/>
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '6px' }}>
                                                <input type="text" style={getInputStyle(index, 'subsidyPrice')} value={formatDisplayValue(item.subsidyPrice)} onChange={(e) => handleDoubleDigitInput(e, index, 'subsidyPrice')} onBlur={(e) => handleBlur(e, index, 'subsidyPrice')} onKeyPress={isNumber} onFocus={(e) => e.target.select()} autoComplete="off" disabled={loading}/>
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '6px' }}>
                                                <input type="text" style={getInputStyle(index, 'stateSubsidyPrice')} value={formatDisplayValue(item.stateSubsidyPrice)} onChange={(e) => handleDoubleDigitInput(e, index, 'stateSubsidyPrice')} onBlur={(e) => handleBlur(e, index, 'stateSubsidyPrice')} onKeyPress={isNumber} onFocus={(e) => e.target.select()} autoComplete="off" disabled={loading}/>
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '6px' }}>
                                                <input type="text" style={getInputStyle(index, 'commSelPrice')} value={formatDisplayValue(item.commSelPrice)} readOnly disabled={loading}/>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td colSpan={totalColumns} style={{ textAlign: "center", padding: '8px' }}>
                                            <button type="submit" disabled={loading} className="button button2">
                                                SUBMIT
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchemeWiseCommoditiesSubsidies;